import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { MessageSquarePlus, ShieldCheck, Users, BookOpen } from "lucide-react";
import ContributionCard from "@/components/community/ContributionCard";
import ContributionModal from "@/components/community/ContributionModal";

const ALL = "all";

export default function AllContributions() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [category, setCategory] = useState(ALL);
  const [moduleFilter, setModuleFilter] = useState(ALL);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== ALL) params.set("category", category);
      if (moduleFilter !== ALL) params.set("module_id", moduleFilter);
      if (verifiedOnly) params.set("verified", "true");
      const { data } = await api.get(`/contributions?${params.toString()}`);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [category, moduleFilter, verifiedOnly]);

  useEffect(() => {
    api.get("/contributions/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    if (!user || user === false) { nav("/login"); return; }
    setModalOpen(true);
  };

  const onUpvote = async (item) => {
    try {
      const { data } = await api.post(`/contributions/${item.id}/upvote`);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, has_upvoted: data.has_upvoted, upvote_count: data.upvote_count } : x));
    } catch (e) {
      if (e?.response?.status === 401) { nav("/login"); return; }
      toast.error(formatApiError(e));
    }
  };
  const onRemoveUpvote = async (item) => {
    try {
      const { data } = await api.delete(`/contributions/${item.id}/upvote`);
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, has_upvoted: data.has_upvoted, upvote_count: data.upvote_count } : x));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const onDelete = async (item) => {
    if (!window.confirm("Delete this contribution?")) return;
    try {
      await api.delete(`/contributions/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-14">
      <div className="mb-10 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">
            <Users className="w-3.5 h-3.5" /> Community
          </div>
          <h1 data-testid="all-contributions-title" className="font-serif text-5xl lg:text-6xl font-black text-[#0A0A0A] tracking-tight leading-none mb-4">
            Wisdom from<br/>people who <em className="italic font-normal">bought.</em>
          </h1>
          <p className="text-lg text-[#525252] leading-relaxed max-w-2xl">
            Verified firsthand stories from investors and recent buyers across India and Dubai. Filtered, upvoted, and ordered by how helpful they were.
          </p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <button
            data-testid="all-contribute-cta"
            onClick={openModal}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#C5A059] text-[#0A0A0A] hover:bg-[#A88643] transition-colors font-medium text-sm btn-primary-hover"
          >
            <MessageSquarePlus className="w-4 h-4" /> Share your experience
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-8 border border-[#E5E5E0] bg-white p-5 space-y-4" data-testid="all-filters">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">Category</span>
          {[{ id: ALL, label: "All" }, ...categories].map((c) => (
            <button
              key={c.id}
              data-testid={`all-filter-cat-${c.id}`}
              onClick={() => setCategory(c.id)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
                category === c.id
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-white"
                  : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">Module</span>
          <button
            data-testid="all-filter-mod-all"
            onClick={() => setModuleFilter(ALL)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
              moduleFilter === ALL ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A]"
            }`}
          >
            All modules
          </button>
          {modules.map((m) => (
            <button
              key={m.id}
              data-testid={`all-filter-mod-${m.id}`}
              onClick={() => setModuleFilter(m.id)}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
                moduleFilter === m.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A]"
              }`}
            >
              {m.number}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252]">Show</span>
          <button
            data-testid="all-filter-verified"
            onClick={() => setVerifiedOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${
              verifiedOnly
                ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0A]"
                : "border-[#E5E5E0] text-[#525252] hover:border-[#0A0A0A]"
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Verified only
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="all-empty" className="border border-dashed border-[#E5E5E0] p-12 text-center bg-[#F5F5F0]">
          <ShieldCheck className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[#525252] mb-4">
            {verifiedOnly
              ? "No verified insights match these filters yet. Turn off ‘Verified only’ to see all community contributions."
              : "No contributions match these filters yet."}
          </p>
          <button onClick={openModal} className="px-4 py-2 bg-[#0A0A0A] text-white text-sm hover:bg-black transition-colors">
            Share the first one
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="all-contributions-list">
          {items.map((it) => (
            <div key={it.id}>
              <div className="mb-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[#525252]">
                <BookOpen className="w-3 h-3" />
                <Link to={`/modules/${it.module_id}`} className="hover:text-[#0A0A0A] transition-colors">
                  {it.module_title}
                </Link>
              </div>
              <ContributionCard item={it} onUpvote={onUpvote} onRemoveUpvote={onRemoveUpvote} onDelete={onDelete} />
            </div>
          ))}
        </div>
      )}

      <ContributionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        moduleId={null}
        onCreated={(created) => {
          setItems((prev) => [created, ...prev]);
          if (verifiedOnly && !created.is_verified) {
            toast.info("Your insight is live. It will appear here once verified.");
          }
        }}
      />
    </div>
  );
}
